'use client';

import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

export default function MagicCursor() {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    const [isVisible, setIsVisible] = useState(false);

    // Smooth spring physics for the cursor follower
    const springConfig = { damping: 25, stiffness: 700 };
    const cursorX = useSpring(mouseX, springConfig);
    const cursorY = useSpring(mouseY, springConfig);

    useEffect(() => {
        // Disable on touch devices
        if (window.matchMedia('(pointer: coarse)').matches || 'ontouchstart' in window) {
            return;
        }

        const moveCursor = (e: MouseEvent) => {
            mouseX.set(e.clientX - 16);
            mouseY.set(e.clientY - 16);
            if (!isVisible) setIsVisible(true);
        };

        const handleMouseDown = () => document.body.classList.add('cursor-clicked');
        const handleMouseUp = () => document.body.classList.remove('cursor-clicked');

        window.addEventListener('mousemove', moveCursor);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mousemove', moveCursor);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [mouseX, mouseY, isVisible]);

    if (!isVisible) return null;

    return (
        <motion.div
            className="pointer-events-none fixed top-0 left-0 z-50 mix-blend-screen"
            style={{ x: cursorX, y: cursorY }}
        >
            <div className="relative">
                {/* Main Cursor Glow */}
                <div className="w-8 h-8 rounded-full bg-amber-400 blur-xl opacity-50" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />

                {/* Trailing Particles (Simulated via CSS for perf) */}
                <div className="absolute top-1/2 left-1/2 w-full h-full animate-ping opacity-20 bg-purple-500 rounded-full" />
            </div>
        </motion.div>
    );
}
